/* eslint-disable max-len */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { hideRegLoginLightbox, showRegLoginLightbox, setRegLoginRedirectUrl } from '@zillow/user-session-store';
import { Link as v4Link } from 'react-router-dom';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import api from 'app/shared/utils/api';
import { Anchor, Input, TextButton } from '@zillow/constellation';

import Button from 'app/shared/components/LandingPageRefresh/Button';
import Carousel from 'app/shared/components/LandingPageRefresh/Carousel';
import Checklist from 'app/shared/components/LandingPageRefresh/Checklist';
import ContentWrapper from 'app/shared/components/LandingPageRefresh/ContentWrapper';
import CrossPromoContainer from 'app/shared/components/LandingPageRefresh/CrossPromoContainer';
import CrossPromoContent from 'app/shared/components/LandingPageRefresh/CrossPromoContent';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import Hero from 'app/shared/components/LandingPageRefresh/Hero';
import Image from 'app/shared/components/LandingPageRefresh/Image';
import ImageBackground from 'app/shared/components/LandingPageRefresh/ImageBackground';
import ImageContainer from 'app/shared/components/LandingPageRefresh/ImageContainer';
import ImageOverlay from 'app/shared/components/LandingPageRefresh/ImageOverlay';
import Link from 'app/shared/components/LandingPageRefresh/Link';
import MoreButton from 'app/shared/components/LandingPageRefresh/MoreButton';
import PlayVideoIcon from 'app/shared/images/PlayVideo.svg';
import PromoBlock from 'app/shared/components/LandingPageRefresh/PromoBlock';
import ReviewContent from 'app/shared/components/LandingPageRefresh/ReviewContent';
import TextBlock from 'app/shared/components/LandingPageRefresh/TextBlock';
import TextContainer from 'app/shared/components/LandingPageRefresh/TextContainer';
import Title from 'app/shared/components/LandingPageRefresh/Title';
import Tooltip from 'app/shared/components/LandingPageRefresh/Tooltip';
import VideoLightbox from 'app/shared/components/LandingPageRefresh/VideoLightbox';

import numberUtils from 'app/shared/utils/numberUtils';
import { PropertiesSelectors, UserSelectors } from 'app/shared/selectors';
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';

import { RMWContext } from 'app/shared/providers/RMWContext';

import Images from 'app/shared/images/landingPageV2';
import * as S from './styles';

const CAMPAIGN_ID = 'ZRM_Web_AppDownload_May2020_Product_7T6tTU';
const pageTitle = 'Property Management Tools for Landlords';
const pageMetaDescription = 'Use our property management tools to post listings, '
    + 'manage rental applications, screen tenants and collect rent payments all with Zillow Rental Manager.';

const VIEW_PROPERTIES_URL_QUERY = '?itc=zrm_view_your_properties_main_zrm_start';
const POST_FIRST_LISTING_URL_QUERY = '?postingPath=true&itc=ltr_zw_zw_zrm-home_btn_post-your-first-listing-here';

const LandingPageV2 = () => {
    const dispatch = useDispatch();
    const [showVideoLightbox, setShowVideoLightbox] = useState(false);
    const properties = useSelector(state => PropertiesSelectors.makeProperties(state));
    const isLoggedIn = useSelector(state => UserSelectors.makeIsLoggedIn(state));
    const { paths } = React.useContext(RMWContext);
    const hasAnyListings = !!properties.numTotalListings;
    const [phoneNum, setPhoneNum] = useState('');
    const [phoneNumError, setPhoneNumError] = useState(false);
    const [marketingTextSent, setMarketingTextSent] = useState(false);

    useEffect(() => {
        window.scroll(0, 0);

        return () => {
            hideRegLoginLightbox();
        };
    }, []);

    const trackGaEvent = (eventName) => {
        switch (eventName) {
            case 'HeroCtaPostListingFree': {
                dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.MAIN_GET_STARTED));
                break;
            }
            case 'HeroCtaViewProperties': {
                dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.MAIN_VIEW_YOUR_PROPERTIES));
                break;
            }
            case 'HeroCtaSignIn': {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Text`,
                    label: 'ClickThrough',
                    action: `SignIn${DELIMITER}Hero`,
                }));
                break;
            }
            case `GetStarted${DELIMITER}Module1`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Button`,
                    label: 'ClickThrough',
                    action: `GetStarted${DELIMITER}Module1`,
                }));
                break;
            }
            case `LearnMore${DELIMITER}PostListingModule`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Text`,
                    label: 'ClickThrough',
                    action: `LearnMore${DELIMITER}PostListingModule`,
                }));
                break;
            }
            case `AcceptApplications${DELIMITER}Module`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Button`,
                    label: 'ClickThrough',
                    action: `AcceptApplications${DELIMITER}Module`,
                }));
                break;
            }
            case `LearnMore${DELIMITER}RentalApplicationModule`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Text`,
                    label: 'ClickThrough',
                    action: `LearnMore${DELIMITER}RentalApplicationModule`,
                }));
                break;
            }
            case `CreateLease${DELIMITER}Module`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Button`,
                    label: 'ClickThrough',
                    action: `CreateLease${DELIMITER}Module`,
                }));
                break;
            }
            case `LearnMore${DELIMITER}RentalLeaseModule`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Text`,
                    label: 'ClickThrough',
                    action: `LearnMore${DELIMITER}RentalLeaseModule`,
                }));
                break;
            }
            case `GetPaid${DELIMITER}Module`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Button`,
                    label: 'ClickThrough',
                    action: `GetPaid${DELIMITER}Module`,
                }));
                break;
            }
            case `LearnMore${DELIMITER}CollectRentModule`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Text`,
                    label: 'ClickThrough',
                    action: `LearnMore${DELIMITER}CollectRentModule`,
                }));
                break;
            }
            case `GetStarted${DELIMITER}Module5`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Button`,
                    label: 'ClickThrough',
                    action: `GetStarted${DELIMITER}Module5`,
                }));
                break;
            }
            case `GetStarted${DELIMITER}Module6`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}Button`,
                    label: 'ClickThrough',
                    action: `GetStarted${DELIMITER}Module6`,
                }));
                break;
            }
            case `AppDownload${DELIMITER}BottomModule`: {
                dispatch(analyticsUtils.track.GAEventV2({
                    category: `CallToAction${DELIMITER}AppDownload`,
                    label: 'submit',
                    action: `appDownload${DELIMITER}BottomModule`,
                }));
                break;
            }
            default: {
                console.warn('No case for trackingGaEvent');
                break;
            }
        }
    };

    const trackGaAndOptimizelyEvent = (gaEvent) => {
        trackGaEvent(gaEvent);

        analyticsUtils.track.optimizelyCTAEvent();
    };

    const handleSignInClick = (e) => {
        e.preventDefault();
        showRegLoginLightbox();


        trackGaEvent('HeroCtaSignIn');
    };

    const handleNavigationToSensitivePage = ({ e, eventName, url }) => {
        // track event
        trackGaAndOptimizelyEvent(eventName);

        if (!isLoggedIn) {
            // block navigation
            e.preventDefault();

            // set redirectUrl for login success
            setRegLoginRedirectUrl(url);

            // open login modal
            showRegLoginLightbox();
        }
    };

    const handlePhoneClick = () => {
        if (phoneNum) {
            // API doesn't accept phone number formatted like: (555) 123-4567
            // Strip all chars out and send raw number: 5551234567
            const rawNumber = phoneNum.replace(/\D/g, '');

            if (rawNumber.length !== 10) {
                setPhoneNum('');
                setPhoneNumError('Please enter a valid phone number.');

                return;
            }

            dispatch(api.marketing.smsAppLead(rawNumber, CAMPAIGN_ID))
                .then((result = {}) => {
                    const { errors = []} = result;

                    if (!isEmpty(errors)) {
                        setPhoneNum('');
                        setPhoneNumError('An error occurred, please try again.');
                    } else {
                        setPhoneNum('');
                        setPhoneNumError(false);
                        setMarketingTextSent(true);
                        trackGaEvent(`AppDownload${DELIMITER}BottomModule`);
                    }
                });
        }
    };

    const handlePhoneInput = (e) => {
        setPhoneNum(e.target.value);
    };

    return (
        <S.LandingPage noPadding={true} className="LandingPageV2">
            <HeadWrapper
                title={pageTitle}
                description={pageMetaDescription}
            />
            <Hero.Wrapper id="LandingPage-Hero" backgroundImage={Images.HomeHero}>
                <Hero.Content>
                    <Title.H1 id="LandingPage-Title">
                        List your rental. Screen tenants.
                        <br />
                        Sign a lease. Get paid.
                    </Title.H1>
                    <Hero.Subtext id="LandingPage-Subtitle">
                        All in one place with Zillow Rental Manager.
                    </Hero.Subtext>
                    {showVideoLightbox && (
                        <VideoLightbox handleClose={() => setShowVideoLightbox(false)}>
                            <S.VideoLightBoxContainer>
                                <iframe src="https://player.vimeo.com/video/474872311?autoplay=1&app_id=122963&playsinline=0" width="100%" height="100%" frameBorder="0" allow="autoplay; fullscreen" title="Zillow Rental Manager" data-ready="true" webkitallowfullscreen={true} mozallowfullscreen={true} allowFullScreen={true} />
                            </S.VideoLightBoxContainer>
                            <S.VideoLightboxCta>
                                <S.VideoLightBoxCtaTitle>
                                    List your rental. Screen tenants. Sign a lease. Get paid.
                                </S.VideoLightBoxCtaTitle>
                                <S.VideoLightBoxSubtitle>All in one place with Zillow Rental Manager.</S.VideoLightBoxSubtitle>
                                <div>
                                    <Link.PromoLink
                                        onClick={() => trackGaAndOptimizelyEvent('HeroCtaPostListingFree')}
                                        reactRouterLink={v4Link}
                                        to={`${paths.propertiesPage}${POST_FIRST_LISTING_URL_QUERY}`}
                                    >
                                        <Button.Hero buttonType="primary" size="lg">
                                    Post your first listing free
                                        </Button.Hero>
                                    </Link.PromoLink>
                                </div>
                            </S.VideoLightboxCta>
                        </VideoLightbox>
                    )}
                    {hasAnyListings
                        ? (
                            <Link.PromoLink
                                onClick={e => handleNavigationToSensitivePage({
                                    e,
                                    eventName: 'HeroCtaViewProperties',
                                    url: `${paths.propertiesPage}${VIEW_PROPERTIES_URL_QUERY}`,
                                })}
                                to={`${paths.propertiesPage}${VIEW_PROPERTIES_URL_QUERY}`}
                            >
                                <Button.Hero buttonType="primary" size="lg">
                                    View your properties
                                </Button.Hero>
                            </Link.PromoLink>
                        ) : (
                            <>
                                <Link.PromoLink
                                    onClick={e => handleNavigationToSensitivePage({
                                        e,
                                        eventName: 'HeroCtaPostListingFree',
                                        url: `${paths.propertiesPage}${POST_FIRST_LISTING_URL_QUERY}`,
                                    })}
                                    to={`${paths.propertiesPage}${POST_FIRST_LISTING_URL_QUERY}`}
                                >
                                    <Button.Hero buttonType="primary" size="lg">
                                    Post your first listing free
                                    </Button.Hero>
                                </Link.PromoLink>
                                <Button.Video onClick={() => setShowVideoLightbox(!showVideoLightbox)} id="HomePage-VideoButton">
                                    <Button.Image src={PlayVideoIcon} />
                                    See how it works
                                </Button.Video>
                            </>
                        )}
                    {!isLoggedIn && (
                        <Hero.HasAccount>
                            Already have an account?
                            {' '}
                            <Anchor
                                as="a"
                                onClick={handleSignInClick}
                                href={paths.signInPage('/rental-manager/properties?postingPath=true')}
                                inverse={true}
                            >
                                <strong>Sign in</strong>
                            </Anchor>
                        </Hero.HasAccount>
                    )}
                </Hero.Content>
                <MoreButton />
            </Hero.Wrapper>
            <PromoBlock padding="50px 0">
                <CrossPromoContainer margin="0 auto">
                    <CrossPromoContent>
                        <Image
                            src={Images.HomeBlockOne}
                            src2x={Images.HomeBlockOne2x}
                            alt=""
                            style={{ paddingBottom: '40px' }}
                        />
                        <S.CrossPromoTitle>
                            Fill vacancies and
                            <br />
                            manage properties
                        </S.CrossPromoTitle>
                        <TextBlock forceAlign="center" marginTop="20px" padding="0 50px">
                            Landlording made simple, whether you have one rental or an entire portfolio.
                        </TextBlock>
                    </CrossPromoContent>
                    <CrossPromoContent>
                        <Image
                            src={Images.HomeBlockTwo}
                            src2x={Images.HomeBlockTwo2x}
                            alt=""
                            style={{ paddingBottom: '40px' }}
                        />
                        <S.CrossPromoTitle>
                            Over 34 million
                            <br />
                            visitors each month
                        </S.CrossPromoTitle>
                        <TextBlock forceAlign="center" margin="20px auto 0 auto" padding="0 50px">
                            Your property will be listed across Zillow, Trulia and HotPads
                            {' '}
                            to help you find your perfect renter.
                        </TextBlock>
                    </CrossPromoContent>
                    <CrossPromoContent>
                        <Image
                            src={Images.HomeBlockThree}
                            src2x={Images.HomeBlockThree2x}
                            alt=""
                            style={{ paddingBottom: '40px' }}
                        />
                        <S.CrossPromoTitle>
                            A brand you
                            <br />
                            can trust
                        </S.CrossPromoTitle>
                        <TextBlock forceAlign="center" marginTop="20px" padding="0 50px">
                            Landlord tools built by industry experts and the brand that knows real estate.
                        </TextBlock>
                    </CrossPromoContent>
                </CrossPromoContainer>
            </PromoBlock>
            <PromoBlock forceAlign="center" padding="25px 0">
                <Title.H2>
                    It&apos;s easier than ever to be a landlord
                </Title.H2>
                <TextBlock forceAlign="center" marginTop="20px" padding="0 50px" maxWidth="700px">
                    <div>
                        Save time with our property management tools that help you get what you need
                        {' '}
                         — signed leases and rent payments.
                    </div>
                </TextBlock>
            </PromoBlock>

            <PromoBlock>
                <ContentWrapper>
                    <TextContainer>
                        <Title.H3>
                            Post a rental listing
                        </Title.H3>
                        <TextBlock>
                            Easy-to-use tools let you list your property where millions of renters
                            {' '}
                            search each month. Plus, messaging and tenant screening are integrated
                            {' '}
                             into our listing platform to help you find and screen applicants faster.
                        </TextBlock>
                        <TextBlock margin="30px 0 0 0">
                            <S.Inline>
                                <Link.PromoLink
                                    onClick={e => handleNavigationToSensitivePage({
                                        e,
                                        eventName: `GetStarted${DELIMITER}Module1`,
                                        url: `${paths.propertiesPage}?postingPath=true&itc=ltr_zw_zw_zrm-home_btn_get-started`,
                                    })}
                                    to={`${paths.propertiesPage}?postingPath=true&itc=ltr_zw_zw_zrm-home_btn_get-started`}
                                >
                                    <Button.Secondary
                                        buttonType="secondary"
                                        size="lg"
                                    >
                                        Get Started
                                    </Button.Secondary>
                                </Link.PromoLink>
                            </S.Inline>
                            <S.Inline margin="0 0 0 26px">
                                <TextButton
                                    as={v4Link}
                                    onClick={() => trackGaEvent(`LearnMore${DELIMITER}PostListingModule`)}
                                    to={`${paths.postAListingPage}/`}
                                >
                                    Learn more
                                </TextButton>
                            </S.Inline>
                        </TextBlock>
                    </TextContainer>
                    <ImageContainer>
                        <ImageOverlay align="left">
                            <Image
                                src={Images.HomeBlockThreeOverlay}
                                src2x={Images.HomeBlockThreeOverlay2x}
                                alt="Adding property details to a listing. Showing rent price,
                            date available, and lease duration."
                            />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.HomeBlockThreeOverlay}
                            extraSmall2x={Images.HomeBlockThreeOverlay2x}
                            small={Images.HomeBlockThreeWide}
                            small2x={Images.HomeBlockThreeWide2x}
                            medium={Images.HomeBlockThreeWide}
                            medium2x={Images.HomeBlockThreeWide2x}
                            extraLarge={Images.HomeBlockThreeImage}
                            extraLarge2x={Images.HomeBlockThreeImage2x}
                        />
                    </ImageContainer>
                </ContentWrapper>
            </PromoBlock>

            <PromoBlock>
                <ContentWrapper>
                    <ImageContainer>
                        <ImageOverlay align="right">
                            <Image
                                src={Images.HomeBlockFourOverlay}
                                src2x={Images.HomeBlockFourOverlay2x}
                                alt="Renter application summary screenshot. Showing applicant names,
                            and dates submitted. Total income, income to rent, move in date, and
                            number of occupants."
                            />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.HomeBlockFourOverlay}
                            extraSmall2x={Images.HomeBlockFourOverlay2x}
                            small={Images.HomeBlockFourWide}
                            small2x={Images.HomeBlockFourWide2x}
                            medium={Images.HomeBlockFourWide}
                            medium2x={Images.HomeBlockFourWide2x}
                            extraLarge={Images.HomeBlockFourImage}
                            extraLarge2x={Images.HomeBlockFourImage2x}
                        />
                    </ImageContainer>
                    <TextContainer>
                        <Title.H3>
                            Screen tenants with online rental applications
                        </Title.H3>
                        <TextBlock>
                            Sign your next tenant with confidence using our online rental applications.
                            {' '}
                            Save time with credit reports, background checks, eviction history and income verification – all in one place.
                            <Tooltip>
                                In some cases, application components, such as background checks or credit reports, are modified to reflect state and local legal requirements. See more information
                                {' '}
                                <Anchor
                                    inverse={true}
                                    href="https://www.zillow.com/info/limits-rental-applications-reports/"
                                >
                                    here
                                </Anchor>
                            </Tooltip>
                        </TextBlock>
                        <TextBlock margin="30px 0 0 0">
                            <S.Inline>
                                <Link.PromoLink
                                    onClick={e => handleNavigationToSensitivePage({
                                        e,
                                        eventName: `AcceptApplications${DELIMITER}Module`,
                                        url: `${paths.propertiesPage}?mktPath=applications&itc=ltr_zw_zw_zrm-home_btn_accept-applications`,
                                    })}
                                    to={`${paths.propertiesPage}?mktPath=applications&itc=ltr_zw_zw_zrm-home_btn_accept-applications`}
                                >
                                    <Button.Secondary buttonType="secondary" size="lg">Accept applications</Button.Secondary>
                                </Link.PromoLink>
                            </S.Inline>
                            <S.Inline margin="0 0 0 26px">
                                <TextButton
                                    as="a"
                                    onClick={() => trackGaEvent(`LearnMore${DELIMITER}RentalApplicationModule`)}
                                    href="https://www.zillow.com/z/rental-manager/tenant-screening/"
                                >
                                    Learn more
                                </TextButton>
                            </S.Inline>
                        </TextBlock>
                    </TextContainer>
                </ContentWrapper>
            </PromoBlock>

            <PromoBlock>
                <ContentWrapper>
                    <TextContainer>
                        <Title.H3>
                            Sign a rental lease
                        </Title.H3>
                        <TextBlock>
                            Create your rental lease using our online lease builder and templates drafted with local law firms, or upload your own lease and e-sign it with your renters.
                            <Tooltip>
                                Lease builder currently available in Colorado, Florida, Oregon, and select portions of Illinois. Excludes Cook County, Chicago, Aurora, Joliet, Evanston, Mount Prospect, Oak Park, and Elgin.
                            </Tooltip>
                        </TextBlock>
                        <TextBlock margin="30px 0 0 0">
                            <S.Inline>
                                <Link.PromoLink
                                    onClick={e => handleNavigationToSensitivePage({
                                        e,
                                        eventName: `CreateLease${DELIMITER}Module`,
                                        url: `${paths.selectPropertyPage}?type=leases&itc=ltr_zw_zw_zrm-home_btn_create-a-lease`,
                                    })}
                                    to={`${paths.selectPropertyPage}?type=leases&itc=ltr_zw_zw_zrm-home_btn_create-a-lease`}
                                >
                                    <Button.Secondary buttonType="secondary" size="lg">Create a lease</Button.Secondary>
                                </Link.PromoLink>
                            </S.Inline>
                            <S.Inline margin="0 0 0 26px">
                                <TextButton
                                    as="a"
                                    onClick={() => trackGaEvent(`LearnMore${DELIMITER}RentalLeaseModule`)}
                                    href="https://www.zillow.com/z/rental-manager/rental-lease-agreements/"
                                >
                                    Learn more
                                </TextButton>
                            </S.Inline>
                        </TextBlock>
                    </TextContainer>
                    <ImageContainer>
                        <ImageOverlay align="left">
                            <Image src={Images.HomeBlockFiveOverlay} src2x={Images.HomeBlockFiveOverlay2x} alt="Lease creation screenshot. The user has selected a fixed term over a month-to-month term." />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.HomeBlockFiveOverlay}
                            extraSmall2x={Images.HomeBlockFiveOverlay2x}
                            small={Images.HomeBlockFiveWide}
                            small2x={Images.HomeBlockFiveWide2x}
                            medium={Images.HomeBlockFiveWide}
                            medium2x={Images.HomeBlockFiveWide2x}
                            extraLarge={Images.HomeBlockFiveImage}
                            extraLarge2x={Images.HomeBlockFiveImage2x}
                        />
                    </ImageContainer>
                </ContentWrapper>
            </PromoBlock>

            <PromoBlock>
                <ContentWrapper>
                    <ImageContainer>
                        <ImageOverlay align="right">
                            <Image src={Images.HomeBlockSixOverlay} src2x={Images.HomeBlockSixOverlay2x} alt="Rent Payments screenshot. Showing rental payments upcoming, pending, and completed" />
                        </ImageOverlay>
                        <ImageBackground
                            extraSmall={Images.HomeBlockSixOverlay}
                            extraSmall2x={Images.HomeBlockSixOverlay2x}
                            small={Images.HomeBlockSixWide}
                            small2x={Images.HomeBlockSixWide2x}
                            medium={Images.HomeBlockSixWide}
                            medium2x={Images.HomeBlockSixWide2x}
                            extraLarge={Images.HomeBlockSixImage}
                            extraLarge2x={Images.HomeBlockSixImage2x}
                        />
                    </ImageContainer>
                    <TextContainer>
                        <Title.H3>
                            Collect rent payments
                        </Title.H3>
                        <TextBlock>
                            The convenient way for landlords to get paid. Rent, utilities, move-in fees and more are deposited straight into your bank account — at no cost to you.
                        </TextBlock>
                        <TextBlock margin="30px 0 0 0">
                            <S.Inline>
                                <Link.PromoLink
                                    onClick={e => handleNavigationToSensitivePage({
                                        e,
                                        eventName: `GetPaid${DELIMITER}Module`,
                                        url: `${paths.selectPropertyPage}?type=payments&itc=ltr_zw_zw_zrm-home_btn_get-paid`,
                                    })}
                                    to={`${paths.selectPropertyPage}?type=payments&itc=ltr_zw_zw_zrm-home_btn_get-paid`}
                                >
                                    <Button.Secondary buttonType="secondary" size="lg">Get paid</Button.Secondary>
                                </Link.PromoLink>
                            </S.Inline>
                            <S.Inline margin="0 0 0 26px">
                                <TextButton
                                    as="a"
                                    onClick={() => trackGaEvent(`LearnMore${DELIMITER}CollectRentModule`)}
                                    href="https://www.zillow.com/z/rental-manager/collect-rent-online/"
                                >
                                    Learn more
                                </TextButton>
                            </S.Inline>
                        </TextBlock>
                    </TextContainer>
                </ContentWrapper>
            </PromoBlock>

            <PromoBlock backgroundColor="#F9F9F9">
                <ContentWrapper>
                    <TextContainer>
                        <Title.H3>
                            Your first listing is free
                        </Title.H3>
                        <TextBlock>
                            <Title.H4>Every listing includes:</Title.H4>
                            <div>
                                <Checklist>Posting to Zillow, Trulia and HotPads</Checklist>
                                <Checklist>
                                    Tenant screening with background and credit checks
                                    {' '}
                                    <Tooltip>
                                        In some cases, application components,
                                        such as background checks or credit
                                        reports, are modified to reflect state
                                        and local legal requirements. See more
                                        information
                                        {' '}
                                        <Anchor
                                            inverse={true}
                                            href="https://www.zillow.com/info/limits-rental-applications-reports/"
                                        >
                                            here
                                        </Anchor>
                                    </Tooltip>
                                </Checklist>
                                <Checklist>
                                    Online leases
                                    {' '}
                                    <Tooltip>
                                        Currently available in Colorado, Florida, Oregon, and select portions of Illinois.
                                    </Tooltip>
                                </Checklist>
                                <Checklist>Online rent payments</Checklist>
                            </div>
                            <S.PaidListingMessage>
                                <span><i>Additional active listings are $9.99/week and are backed by our Lead Guarantee.</i></span>
                                {' '}
                                <Tooltip>
                                    If you don&apos;t receive a lead within a week, it&apos;s free to list until you do (for up to 8 weeks).
                                </Tooltip>
                            </S.PaidListingMessage>
                            {' '}
                        </TextBlock>
                        <TextBlock margin="30px 0 0 0">
                            <Link.PromoLink
                                onClick={e => handleNavigationToSensitivePage({
                                    e,
                                    eventName: `GetStarted${DELIMITER}Module5`,
                                    url: `${paths.propertiesPage}?postingPath=true`,
                                })}
                                to={`${paths.propertiesPage}?postingPath=true`}
                            >
                                <Button.Primary size="lg">Get Started</Button.Primary>
                            </Link.PromoLink>
                        </TextBlock>
                    </TextContainer>
                    <ImageContainer>
                        <Image src={Images.HomeBlockSevenImage} alt="" />
                    </ImageContainer>
                </ContentWrapper>
            </PromoBlock>

            <PromoBlock>
                <Title.H2>
                    Here&rsquo;s what others are saying
                </Title.H2>
                <Carousel>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            &quot;The number and quality of inquiries was amazing. Very pleased by how user-friendly and fast it is.&quot;
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            Amanda, Landlord, Portland, OR
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            &quot;Zillow Rental Manager has been an excellent source of rental leads for my business. In fact, it&apos;s been the main source of leads. I love this tool and recommend it to everyone.&quot;
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            Kate, Property Manager, Cleveland, OH
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            “Excellent results. Tons of responses. Rented on the first day.”
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            John, Landlord, Boulder, CO
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                    <ReviewContent.Wrapper>
                        <ReviewContent.Body>
                            “Zillow Rental Manager is awesome! Rented two houses in two days!”
                        </ReviewContent.Body>
                        <ReviewContent.Byline>
                            Jeff, Property Manager, Chicago, IL
                        </ReviewContent.Byline>
                    </ReviewContent.Wrapper>
                </Carousel>
            </PromoBlock>

            <Hero.Wrapper backgroundImage={Images.CtaBackground} height="400px">
                <Hero.Content>
                    <Title.H2 color="#fff">Rent with confidence</Title.H2>
                    <Hero.SubtextNarrow>
                        Take the hassle out of landlording with Zillow Rental Manager.
                        {' '}
                        Post a listing, screen tenants, sign a lease and collect payments all in one place.
                    </Hero.SubtextNarrow>
                    <Link.PromoLink
                        onClick={e => handleNavigationToSensitivePage({
                            e,
                            eventName: `GetStarted${DELIMITER}Module6`,
                            url: `${paths.propertiesPage}?postingPath=true`,
                        })}
                        to={`${paths.propertiesPage}?postingPath=true`}
                    >
                        <Button.Hero buttonType="primary" size="lg">Get started</Button.Hero>
                    </Link.PromoLink>
                </Hero.Content>
            </Hero.Wrapper>

            <Hero.Wrapper backgroundColor="#001751" height="383px">
                <Hero.Content>
                    <Title.H2 color="#fff">Get the app</Title.H2>
                    <Hero.Subtext>
                        Use Zillow Rental Manager anywhere, anytime
                    </Hero.Subtext>
                    {marketingTextSent ? (
                        <Hero.Subtext>Message sent!</Hero.Subtext>
                    ) :
                        (
                            <S.CtaPhoneInput>
                                <S.CtaInputWrapper>
                                    <Input
                                        error={!!phoneNumError}
                                        onChange={handlePhoneInput}
                                        placeholder="Enter phone number"
                                        type="tel"
                                        value={numberUtils.formatPhoneNumberInput(phoneNum)}

                                    />
                                </S.CtaInputWrapper>
                                <S.CtaButtonWrapper>
                                    <Button.Cta onClick={handlePhoneClick}>Text me the app</Button.Cta>
                                </S.CtaButtonWrapper>
                            </S.CtaPhoneInput>
                        )}
                    <S.PhoneInputError>
                        {phoneNumError || ''}
                    </S.PhoneInputError>
                    <S.CtaAppSubtext>
                        US mobile numbers only. Message and data rates apply. I agree to receive a one-time text message containing a link to download the Zillow Rental Manager app at the mobile number I provide above, and I confirm that the mobile number is mine.
                    </S.CtaAppSubtext>
                </Hero.Content>
            </Hero.Wrapper>
        </S.LandingPage>
    );
};

export { LandingPageV2 };
export default LandingPageV2;
